// maths.portll.net — invite-only beta wall.
//
// Signed in but no betaAccess → here. The parent types a code, redeemInvite (the
// trusted-plane writer) grants access in a transaction, then we re-read the access
// plane and let the guards carry on to the Grown-Ups' Corner. The server normalises
// the code (trim + upper-case) and hashes it, so we pass it as typed. If this account
// already has beta, we forward straight through rather than make anyone re-enter.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { callRedeemInvite } from "../lib/firebase.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { Button, Card, Field, Note, Spinner, injectStyles } from "../components/ui.jsx";

// redeemInvite HttpsError codes → friendly copy. The Firebase callable surfaces these
// as `functions/<code>` on error.code; we key off the suffix.
function friendlyInviteError(err) {
  const code = String(err?.code || "").replace(/^functions\//, "");
  switch (code) {
    case "invalid-argument":
      return "Type your invite code, then tap Unlock.";
    case "not-found":
      return "That code doesn't look right — check for typos (an O is not a zero) and try again.";
    case "failed-precondition":
      return "This invite has expired. Ask us for a fresh one and we'll sort you out.";
    case "resource-exhausted":
      return "This code has already been fully used. Ask us for your own and we'll get you in.";
    case "unauthenticated":
      return "Your session timed out — please sign in again.";
    default:
      return err?.message || "Something went wrong redeeming that code. Please try again.";
  }
}

export default function InviteWall() {
  const { betaAccess, refreshAccess, logout } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Already have access (e.g. redeemed on another visit)? Skip the wall.
  useEffect(() => {
    if (betaAccess) navigate("/grown-ups", { replace: true });
  }, [betaAccess, navigate]);

  async function redeem(e) {
    e.preventDefault();
    if (busy) return;
    const c = code.trim();
    if (!c) {
      setErr("Type your invite code, then tap Unlock.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await callRedeemInvite({ code: c });
      const ok = await refreshAccess();
      if (ok) navigate("/grown-ups", { replace: true });
      else {
        setErr("That worked, but access hasn't updated just yet. Give it a moment and tap Unlock again.");
        setBusy(false);
      }
    } catch (e2) {
      setErr(friendlyInviteError(e2));
      setBusy(false);
    }
  }

  return (
    <main className="iw-shell">
      <Link to="/" className="iw-brand" aria-label="maths.portll.net home">
        <span aria-hidden="true">🌿</span>
        <span>maths<span className="iw-brand__dot">·</span>portll</span>
      </Link>

      <Card className="iw-card">
        <div className="iw-badge" aria-hidden="true">✉️</div>
        <h1 className="iw-title">You’re on the list</h1>
        <p className="iw-sub">
          maths.portll.net is in a small, invite-only beta. Pop in your code to open the
          Grown-Ups’ Corner.
        </p>

        <form onSubmit={redeem} noValidate>
          <Field label="Invite code" htmlFor="iw-code">
            <input
              id="iw-code"
              className="iw-code"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              placeholder="INVITE CODE"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (err) setErr("");
              }}
              disabled={busy}
            />
          </Field>

          {err && <Note tone="danger">{err}</Note>}

          <Button type="submit" variant="primary" size="lg" className="iw-full" disabled={busy}>
            {busy ? (
              <>
                <Spinner size={18} label="Checking" /> Checking…
              </>
            ) : (
              "Unlock"
            )}
          </Button>
        </form>

        <p className="iw-help">
          No code yet? It’s a friends-and-family beta for now — hang tight, more invites
          are coming.
        </p>
      </Card>

      <button type="button" className="iw-signout no-print" onClick={() => logout()}>
        Wrong account? Sign out
      </button>
    </main>
  );
}

const IW_CSS = `
.iw-shell{min-height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:1.1rem;padding:clamp(1rem,4vw,2.5rem);}
.iw-brand{display:inline-flex;align-items:center;gap:.45rem;text-decoration:none;color:var(--brand-ink);
  font-weight:800;font-size:1.15rem;letter-spacing:-.01em;}
.iw-brand__dot{color:var(--accent);}
.iw-card{width:100%;max-width:25rem;text-align:center;padding:1.6rem 1.4rem 1.3rem;}
.iw-badge{font-size:2rem;line-height:1;}
.iw-title{margin:.5rem 0 0;font-size:clamp(1.35rem,4.5vw,1.7rem);}
.iw-sub{margin:.4rem 0 1.15rem;color:var(--ink-soft);}
.iw-code{text-align:center;letter-spacing:.18em;text-transform:uppercase;font-weight:700;font-size:1.05rem;}
.iw-code::placeholder{letter-spacing:.12em;font-weight:600;color:var(--ink-soft);opacity:.7;}
.iw-full{width:100%;margin-top:.2rem;}
.iw-help{margin:1rem 0 0;font-size:.86rem;color:var(--ink-soft);}
.iw-signout{background:transparent;border:none;min-height:44px;padding:.4rem .8rem;color:var(--ink-soft);
  font-weight:600;font-size:.9rem;}
`;

injectStyles("maths-invite-styles", IW_CSS);
